"use client";
import { useAtom } from "jotai";
import "react-responsive-modal/styles.css";
import { useMatchRoute, useNavigate } from "@tanstack/react-router";
import {
  useAuth,
  useI18N,
  useSearchPageParams,
  useNotifications,
  useSavedSearches,
} from "common/hooks";
import { useHandleSavedSearchSelect } from "@/hooks/useHandleSavedSearchSelect";
import { useDarkMode } from "@/hooks/useDarkMode";
import { drawerMenuOpenAtom } from "@/store/atoms";
import HamburgerMenu from "../profile/HamburgerMenu";
import { useSelectedVenue } from "@/hooks/useSelectedVenue";
import {
  savedPlaceByUserIdToViewAtom,
  savedPlaceIdToViewAtom,
  venueDetailsOpenedAtom,
  viewModeAtom,
  searchOverlayOpenAtom,
  mapCenterAtom,
  searchTermInputAtom,
} from "@/state/atoms";
import { bucketUrlPrefix, defaultRegion, enableMapperSectionsDesktop, showLoginByDefault } from "common/config";
import { resetAllAppState } from "common/utils";
import { NameInitialsAvatar } from "react-name-initials-avatar";
import { actionToPerformAfterLoginAtom } from "@/store/atoms";
import { useEffect, useState, useMemo, useCallback } from "react";
import AnalyticsService from "@/services/analytics";

import { SlidersHorizontal, MagnifyingGlass } from "phosphor-react";
import { cn } from "@/lib/utils";

import { AuthModalScreen, useAuthModals } from "@/hooks/useAuthModals";
import { SearchBar } from "@/components/Search/SearchBar";
import { NotificationPanel } from "@/components/Notifications/NotificationPanel";
import { useSearchDisplayTerm } from "@/hooks/useSearchDisplayTerm";
import {Bell, HeartStraight} from "phosphor-react";

// Routes where the search box should be hidden
// Add route paths here to hide the search box on those routes
const ROUTES_WITHOUT_SEARCH = [
  "/", // home page
  "/accessibility-guide/$venueId", // Accessibility guide venue page
  "/accessibility-guide-space/$venueId/$spaceId", // Accessibility guide space page
] as const;

function Navbar() {
  const i18n = useI18N();
  const { isLoggedIn, user, isMapper } = useAuth();
  const shouldUseMapperView = isMapper && enableMapperSectionsDesktop;
  const navigate = useNavigate();
  const { setSelectedVenue } = useSelectedVenue();
  const isDark = useDarkMode();
  const [, setSavedPlaceIdToView] = useAtom(savedPlaceIdToViewAtom);
  const [, setSavedPlaceByUserIdToView] = useAtom(savedPlaceByUserIdToViewAtom);
  const [, setViewMode] = useAtom(viewModeAtom);
  const { showModal } = useAuthModals();

  // Global search params for syncing with BrandsSection navigation
  const { searchPageParams, setSearchPageParams } = useSearchPageParams();

  // Derive search term from global state using custom hook
  const derivedSearchTerm = useSearchDisplayTerm();

    // Internal state for search term (used in header/overlay layouts)
    const [internalSearchTerm, setInternalSearchTerm] = useAtom(searchTermInputAtom);

  // Calculate active filter count from searchPageParams
  const activeFilterCount = useMemo(() => {
    let count = 0;
    // Count business types (pipe-separated string)
    if (searchPageParams.types && searchPageParams.types.trim()) {
      count += searchPageParams.types.split("|").filter((t) => t.trim()).length;
    }
    // Count accessibility filters (pipe-separated string)
    if (
      searchPageParams.accessibilities &&
      searchPageParams.accessibilities.trim()
    ) {
      count += searchPageParams.accessibilities
        .split("|")
        .filter((a) => a.trim()).length;
    }
    // Count opening hours filter (only when not"any")
    if (
      searchPageParams.openingHoursMode &&
      searchPageParams.openingHoursMode !== "any"
    ) {
      count += 1;
    }
    return count;
  }, [
    searchPageParams.types,
    searchPageParams.accessibilities,
    searchPageParams.openingHoursMode,
  ]);

  // Check if any filters are active
  const hasActiveFilters = activeFilterCount > 0;

  // Parse filter values from searchPageParams for overlay SearchBar
  const parsedAccessibilityOptions = useMemo(() => {
    if (!searchPageParams.accessibilities) return [];
    return searchPageParams.accessibilities
      .split("|")
      .filter((a) => a.trim())
      .map(Number);
  }, [searchPageParams.accessibilities]);

  const parsedBusinessTypes = useMemo(() => {
    if (!searchPageParams.types) return [];
    return searchPageParams.types.split("|").filter((t) => t.trim());
  }, [searchPageParams.types]);

  // Parse opening hours values from searchPageParams for overlay SearchBar
  const parsedOpeningHoursMode = searchPageParams.openingHoursMode || "any";
  const parsedOpeningHoursDay = searchPageParams.openingHoursDay || "";
  const parsedOpeningHoursTime = searchPageParams.openingHoursTime || "";

  // Search state for overlay interaction
  const [isSearchOverlayOpen, setIsSearchOverlayOpen] = useAtom(searchOverlayOpenAtom);
  const [mapCenter] = useAtom(mapCenterAtom);
  const [isNotificationPanelOpen, setIsNotificationPanelOpen] = useState(false);
  
  // Fetch unread notifications count for badge
  const { data: unreadNotificationsData } = useNotifications("all", true);
  const unreadCount = useMemo(() => {
    if (
      !unreadNotificationsData?.pages ||
      unreadNotificationsData.pages.length === 0
    )
      return 0;
    return unreadNotificationsData.pages[0]?.pagination?.total || 0;
  }, [unreadNotificationsData]);

  const [actionToPerformAfterLogin, setActionToPerformAfterLogin] = useAtom(
    actionToPerformAfterLoginAtom,
  );

  useEffect(() => {
    console.log("check actionToPerformAfterLogin");
    if (isLoggedIn && user && actionToPerformAfterLogin) {
      if (actionToPerformAfterLogin === "redirectToMySavePlace") {
        navigate({ to: "/mysavedplaces" });
        setActionToPerformAfterLogin("");
      }
    }
  }, [actionToPerformAfterLogin, user, isLoggedIn]);

  useEffect(() => {
    const timer = setTimeout(() => {
      console.log("isLoggedIn", isLoggedIn, user);
      if (showLoginByDefault === "Yes" && !isLoggedIn && !user) {
        showModal(AuthModalScreen.EnterEmail);
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, [showLoginByDefault, isLoggedIn, user]);

  const handleOpenAuthModal = () => {
    showModal(AuthModalScreen.EnterEmail);
  };

  const matchRoute = useMatchRoute();

  // Determine if the current route matches'/mysavedplaces'
  const isSavedActive =
    matchRoute({ to: "/mysavedplaces" }) ||
    matchRoute({ to: `/savedplacedetails/$savedPlaceId/user/$userId` });

  // Check if search box should be hidden for the current route
  const shouldHideSearch = ROUTES_WITHOUT_SEARCH.some((route) =>
    matchRoute({ to: route }),
  );

  // Fetch saved searches only when SearchBar is shown (not on routes where it's hidden)
  const { data: savedSearchesData } = useSavedSearches({
    enabled: !shouldHideSearch,
  });

  // Flatten all pages into a single array and calculate count
  const savedSearches = useMemo(() => {
    return savedSearchesData?.pages.flatMap((page) => page.searches) ?? [];
  }, [savedSearchesData?.pages]);

  const savedSearchesCount = savedSearches.length;

  const [, setIsVenueDetailsOpen] = useAtom(venueDetailsOpenedAtom);

  const profileUrl = bucketUrlPrefix + (user as any)?.profile_photo;

  const [, setDrawerMenuOpen] = useAtom(drawerMenuOpenAtom);

  const toggleDrawerMenu = () => {
    setDrawerMenuOpen((drawerMenuOpen) => !drawerMenuOpen);
  };

  const clearsetSelectedVenue = () => {
    AnalyticsService.trackGAEventHandler("navigatedToMapView");
    AnalyticsService.trackMx("navigatedToMapView", {});

    setSelectedVenue(null);
    setIsVenueDetailsOpen(false);
    setIsSearchOverlayOpen(false);

    setSavedPlaceIdToView("");
    setSavedPlaceByUserIdToView("");

    // Centralized cleanup of all app state
    resetAllAppState({
      searchPageParams,
      setSearchPageParams,
      setViewMode,
      isMapper,
      setInternalSearchTerm,
    });

    navigate({ to: "/" });
  };

  const handleSavedClick = () => {
    AnalyticsService.trackGAEventHandler("navigatedToMySavedPlaces");
    AnalyticsService.trackMx("navigatedToMySavedPlaces", {});

    setSelectedVenue(null);
    setIsVenueDetailsOpen(false);
    setIsSearchOverlayOpen(false);

    setSavedPlaceIdToView("");
    setSavedPlaceByUserIdToView("");

     // Centralized cleanup of all app state
     resetAllAppState({
      searchPageParams,
      setSearchPageParams,
      setViewMode,
      isMapper,
      setInternalSearchTerm,
    });

    navigate({ to: "/mysavedplaces" });
  };

  const handleNotificationsClick = () => {
    if (!isLoggedIn || !user) {
      // Show login modal if user is not logged in
      showModal(AuthModalScreen.EnterEmail);
      return;
    }
    // Close search overlay before showing notification panel
    setIsSearchOverlayOpen(false);
    // Show notification panel if user is logged in
    AnalyticsService.trackGAEventHandler("navigatedToNotifications");
    AnalyticsService.trackMx("navigatedToNotifications", {});
    setIsNotificationPanelOpen((prev) => !prev);
  };

  const handleNotificationPanelClose = () => {
    setIsNotificationPanelOpen(false);
  };

  const handleViewAllNotifications = () => {
    setIsNotificationPanelOpen(false);
    navigate({ to: "/notifications" });
  };

  const handleManageNotifications = () => {
    setIsNotificationPanelOpen(false);
    navigate({ to: "/settings" });
  };

  // Handle saved search selection
  const handleSavedSearchSelect = useHandleSavedSearchSelect({
    onSuccess: () => setIsSearchOverlayOpen(false),
  });

  const handleSearchBoxClick = () => {
    setIsSearchOverlayOpen(true);
  };

  const handleSearchOverlayClose = () => {
    setInternalSearchTerm(derivedSearchTerm);
    setIsSearchOverlayOpen(false);
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleSearchSubmit = (term: string, count: number) => {
    // The search overlay updates global search params
    // Filter count is now derived from searchPageParams (term and count params kept for interface compatibility)
    setIsSearchOverlayOpen(false);
  };

  // Handle Escape key to close search overlay
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isSearchOverlayOpen) {
        handleSearchOverlayClose();
      }
    };

    if (isSearchOverlayOpen) {
      document.addEventListener("keydown", handleKeyDown);
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isSearchOverlayOpen]);

  return (
    <header className="sticky top-0 z-50 w-full bg-bg-brand-secondary-default border-b border-border-token-brand-minimal">
      <nav
        className="flex items-center justify-between gap-3 px-4 py-4 md:px-6 min-h-20 h-20"
        aria-label="Main navigation"
      >
        {/* Logo Section */}
        <div className="flex flex-1 items-center">
          <button
            type="button"
            onClick={clearsetSelectedVenue}
            className="inline-flex p-0.5 rounded-md focus-blue"
            aria-label="Sociability Home"
          >
            {isDark ? (
              <>
                {/* Full Logo - Hidden on very small screens */}
                <img
                  className="h-10 w-auto hidden logo-small:hidden sm:block"
                  src="/images/Logo_dark.svg"
                  alt=""
                  aria-hidden="true"
                />
                {/* Icon Only Logo - Shown on very small screens */}
                <img
                  className="h-10 w-10 block sm:hidden logo-small:block"
                  src="/images/logo_icon.svg"
                  alt=""
                  aria-hidden="true"
                />
              </>
            ) : (
              <>
                {/* Full Logo - Hidden on very small screens */}
                <img
                  className="h-10 w-auto hidden logo-small:hidden sm:block"
                  src="/images/Logo.svg"
                  alt=""
                  aria-hidden="true"
                />
                {/* Icon Only Logo - Shown on very small screens */}
                <img
                  className="h-10 w-10 block sm:hidden logo-small:block"
                  src="/images/logo_icon.svg"
                  alt=""
                  aria-hidden="true"
                />
              </>
            )}
          </button>
        </div>

        {/* Search Box - Clickable to open overlay, hidden when overlay is open or on specific routes */}
        {!isSearchOverlayOpen && !shouldHideSearch && (
          <button
            type="button"
            onClick={handleSearchBoxClick}
            className={cn(
              "hidden md:flex flex-1 lg:flex-none lg:w-[400px] items-center gap-4 h-[48px] px-4",
              "bg-bg-brand-secondary-default",
              "border border-border-token-neutral-subtle rounded-lg",
              "transition-colors",
              "hover:border-primary-500 focus-blue cursor-pointer",
            )}
            aria-label={
              hasActiveFilters
                ? `${i18n.get("openSearch") || "Open search"}, ${activeFilterCount} ${activeFilterCount === 1 ? "filter" : "filters"} applied`
                : i18n.get("openSearch") || "Open search"
            }
          >
            {/* Search Icon */}
            <MagnifyingGlass
              size={20}
              className="text-fg-neutral-subtle flex-shrink-0"
              strokeWidth={1.5}
              aria-hidden="true"
            />

            {/* Search Text / Placeholder */}
            <span
              className={cn(
                "flex-1 min-w-0 text-left truncate",
                "label-m-medium",
                internalSearchTerm
                  ? "text-fg-neutral-strong"
                  : "text-fg-neutral-subtle",
              )}
            >
              {internalSearchTerm
                ? internalSearchTerm
                : i18n.get("searchVenueOrLocation") ||
                  "Search venue or location"}
            </span>

            {/* Divider - Always visible */}
            <div
              className="h-6 w-px border border-border-token-neutral-minimal flex-shrink-0"
              aria-hidden="true"
            />

            {/* Filter Section - Always visible */}
            <div className="flex items-center gap-2 flex-shrink-0">
              {/* Filter Icon - Primary color when filters are active */}
              <SlidersHorizontal
                size={20}
                className={cn(
                  hasActiveFilters
                    ? "text-fg-neutral-strong"
                    : "text-fg-neutral-subtle",
                )}
                strokeWidth={1.5}
                aria-hidden="true"
              />

              {/* Filter Count Badge - Show when filters are active */}
              {hasActiveFilters && (
                <span
                  className={cn(
                    "flex items-center justify-center min-w-[20px] h-5 rounded-full bg-container-brand-subtle text-fg-neutral-onFilled label-s-medium"
                  )}
                  aria-hidden="true"
                >
                  {activeFilterCount}
                </span>
              )}
            </div>
          </button>
        )}

        {/* Navigation Items Section */}
        <div className="flex flex-1 items-center justify-end gap-2 md:gap-4">
          {/* Notifications Button - Mobile Icon */}
          <button
            type="button"
            onClick={handleNotificationsClick}
            className="md:hidden inline-flex items-center justify-center gap-1 px-2 h-12 rounded-lg hover:bg-bg-brand-secondary-hover transition-colors focus-blue"
            aria-label={
              unreadCount > 0
                ? `${i18n.get("notifications") || "Notifications"}, ${unreadCount} unread`
                : i18n.get("notifications") || "View notifications"
            }
            aria-expanded={isNotificationPanelOpen}
            aria-haspopup="dialog"
          >
            <Bell size={20} className="text-fg-neutral-strong" aria-hidden="true" />
            {unreadCount > 0 && (
              <span
                className={`${
                  unreadCount > 99
                    ? "w-[24px] h-[24px]"
                    : unreadCount > 9
                      ? "w-[22px] h-[22px]"
                      : "w-4 h-4"
                } rounded-full flex justify-center items-center bg-[#8a0000]`}
                aria-hidden="true"
              >
                <span
                  className={`text-center text-neutral-50 ${
                    unreadCount > 99
                      ? "label-s-regular"
                      : unreadCount > 9
                        ? "label-s-regular"
                        : "label-s-medium"
                  }`}
                >
                  {unreadCount > 99 ? "99+" : unreadCount}
                </span>
              </span>
            )}
          </button>

          {/* Notifications Button - Desktop */}
          <button
            type="button"
            onClick={handleNotificationsClick}
            className="hidden md:inline-flex items-center justify-center min-h-[40px] min-w-[44px] px-3 py-2 rounded-lg label-m-medium text-fg-neutral-strong hover:bg-bg-brand-secondary-hover transition-colors focus-blue"
            aria-label={
              unreadCount > 0
                ? `${i18n.get("notifications") || "Notifications"}, ${unreadCount} unread`
                : i18n.get("notifications") || "View notifications"
            }
            aria-expanded={isNotificationPanelOpen}
            aria-haspopup="dialog"
          >
            <span aria-hidden="true">
              {i18n.get("notifications") || "Notifications"}
            </span>

            {unreadCount > 0 && (
              <span
                className="ml-2 h-5 min-w-5 px-1 rounded-full inline-flex justify-center items-center bg-[#8a0000]"
                aria-hidden="true"
              >
                <span className="text-center text-neutral-50 label-s-medium">
                  {unreadCount > 99 ? "99+" : unreadCount}
                </span>
              </span>
            )}
          </button>

          {/* Single Notification Panel - shared between mobile and desktop - only show when logged in */}
          {isLoggedIn && user && (
            <NotificationPanel
              isOpen={isNotificationPanelOpen}
              onClose={handleNotificationPanelClose}
              onViewAllClick={handleViewAllNotifications}
              onManageClick={handleManageNotifications}
              showBackdrop={true}
            />
          )}

          {/* Saved Button - Mobile Icon */}
          <button
            type="button"
            onClick={handleSavedClick}
            className={`md:hidden inline-flex items-center justify-center w-10 h-10 rounded-lg transition-colors focus-blue ${
              isSavedActive
                ? "bg-container-brand-minimal"
                : "hover:bg-bg-brand-secondary-hover"
            }`}
            aria-label={
              isSavedActive
                ? `${i18n.get("saved") || "Saved"} - current page`
                : i18n.get("saved") || "View saved places"
            }
            aria-current={isSavedActive ? "page" : undefined}
          >
            <HeartStraight size={20} className="text-fg-neutral-strong" aria-hidden="true" />
          </button>

          {/* Saved Button - Desktop */}
          <button
            type="button"
            onClick={handleSavedClick}
            className={`hidden md:inline-flex items-center justify-center min-h-[40px] min-w-[44px] px-3 py-2 rounded-lg label-m-medium transition-colors focus-blue ${
              isSavedActive
                ? "text-fg-brand-subtle bg-container-brand-minimal"
                : "text-fg-neutral-strong hover:bg-bg-brand-secondary-hover"
            }`}
            aria-label={
              isSavedActive
                ? `${i18n.get("saved") || "Saved"} - current page`
                : i18n.get("saved") || "View saved places"
            }
            aria-current={isSavedActive ? "page" : undefined}
          >
            <span aria-hidden="true">{i18n.get("saved") || "Saved"}</span>
          </button>

          {/* Profile Avatar Button */}
          {!user?.name ? (
            <>
              {/* Login Button - Desktop only */}
              <button
                type="button"
                onClick={handleOpenAuthModal}
                className="hidden md:inline-flex items-center justify-center h-10 px-6 rounded-lg bg-bg-brand-primary-default hover:bg-bg-brand-primary-hover transition-colors focus-blue"
                aria-label={i18n.get("loginNavbar") || "Log in"}
              >
                <span aria-hidden="true" className="label-m-medium text-white">
                  {i18n.get("loginNavbar") || "Log in"}
                </span>
              </button>

              {/* Profile Placeholder */}
              <button
                type="button"
                onClick={toggleDrawerMenu}
                className="flex-shrink-0 w-10 h-10 rounded-full border border-border-token-brand-minimal overflow-hidden focus-blue"
                aria-label="Open profile menu"
              >
                <img
                  className="w-full h-full object-cover bg-neutral-300"
                  src="/images/user_placeholder.svg"
                  alt="Default profile icon"
                  aria-hidden="true"
                />
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={toggleDrawerMenu}
              className="flex-shrink-0 w-10 h-10 rounded-full border border-border-token-brand-minimal overflow-hidden focus-blue"
              aria-label={`Open profile menu${user?.name ? ` for ${user.name}` : ""}`}
            >
              {(user as any)?.profile_photo ? (
                <img
                  className="w-full h-full object-cover"
                  src={profileUrl}
                  alt=""
                  aria-hidden="true"
                />
              ) : (
                <div aria-hidden="true" className="flex items-center justify-center w-full h-full">
                  <NameInitialsAvatar
                    name={user?.name?.split("")[0]}
                    bgColor={
                      user?.profile_color_code
                        ? user.profile_color_code
                        : "#8751FF"
                    }
                    textColor="white"
                    textSize="16px"
                    size="40px"
                    borderColor={
                      user?.profile_color_code
                        ? user.profile_color_code
                        : "#8751FF"
                    }
                  />
                </div>
              )}
            </button>
          )}
        </div>
      </nav>

      {/* Hamburger Menu Drawer */}
      <div className="relative z-[1020]">
        <HamburgerMenu />
      </div>

      {/* Search Overlay */}
      <SearchBar
        layout="overlay"
        tagging_mode={searchPageParams.tagging_mode}
        isLoggedIn={isLoggedIn}
        isOverlayOpen={isSearchOverlayOpen}
        onOverlayClose={handleSearchOverlayClose}
        onOverlaySearch={handleSearchSubmit}
        initialSearchTerm={derivedSearchTerm}
        selectedAccessibilityOptions={parsedAccessibilityOptions}
        selectedBusinessTypes={parsedBusinessTypes}
        openingHoursMode={parsedOpeningHoursMode}
        selectedDay={parsedOpeningHoursDay}
        selectedTime={parsedOpeningHoursTime}
        searchTerm=""
        savedSearchesCount={savedSearchesCount}
        savedSearches={savedSearches}
        onSearchSubmit={() => {}}
        onSavedSearchesClick={() => {}}
        onSavedSearchSelect={handleSavedSearchSelect}
        latitude={mapCenter.lat || defaultRegion.latitude}
        longitude={mapCenter.lng || defaultRegion.longitude}
        locationLatitude={
          !isNaN(parseFloat(searchPageParams.userlatitude || "")) && isFinite(parseFloat(searchPageParams.userlatitude || ""))
          ? parseFloat(searchPageParams.userlatitude || "") 
          : undefined
        }
        locationLongitude={
          !isNaN(parseFloat(searchPageParams.userlongitude || "")) && isFinite(parseFloat(searchPageParams.userlongitude || ""))
          ? parseFloat(searchPageParams.userlongitude || "") 
          : undefined
        }
      />
    </header>
  );
}

export default Navbar;
